import React from 'react';
import { ArrowForwardIosRounded, CancelRounded, CheckCircleRounded, SwapHorizRounded, SwapVertRounded, TrendingDownRounded, UndoRounded } from '@mui/icons-material';
import { Button, Chip, Divider, Typography } from '@mui/material';
import { Stack, useTheme } from '@mui/system';
import { formatCurrency } from '../../../utils/formatNumbers';

function ItemReplacementEntry({ replacement, onClick }) {
    const theme = useTheme();
    const item = replacement.item || {};
    const proposedItem = replacement.recommended_item || {};
    const recommendations = replacement.recommendations || [];
    const acceptedCount = recommendations.filter((recommendation) => recommendation.status === 'accepted').length;
    const totalCount = recommendations.length;
    const isPartiallyAccepted = replacement.replacement_status === 'partially_reviewed';
    const isAccepted = replacement.replacement_status === 'accepted';
    const isDeclined = replacement.replacement_status === 'rejected';
    const isOpen = !isPartiallyAccepted && !isAccepted && !isDeclined;
    const originalItemOpacity = isAccepted ? 0.45 : 1;
    const proposedItemOpacity = isDeclined ? 0.45 : 1;
    const status = isAccepted ? {
        color: theme.palette.green.taskText,
        icon: <CheckCircleRounded sx={{ fontSize: '14px' }} />,
        label: 'Accepted'
    } : isDeclined ? {
        color: theme.palette.red.main,
        icon: <CancelRounded sx={{ fontSize: '14px' }} />,
        label: 'Declined'
    } : isPartiallyAccepted ? {
        color: theme.palette.yellow.inProgress,
        icon: <SwapHorizRounded sx={{ fontSize: '14px' }} />,
        label: 'Partially Accepted'
    } : {
        color: theme.palette.text.primary,
        icon: <SwapHorizRounded sx={{ fontSize: '14px' }} />,
        label: 'Replace Item'
    };

    return (
        <Stack p={2} spacing={1} sx={{ borderRadius: '8px', backgroundColor: theme.palette.background.white, boxShadow: 1 }}>
            <Stack spacing={1} onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} onKeyDown={onClick ? (event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick();
                }
            } : undefined} sx={{ cursor: onClick ? 'pointer' : 'default' }}>
                <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
                    <Stack direction='row' alignItems='center' spacing={0.5}>
                        <Stack sx={{ color: status.color }}>{status.icon}</Stack>
                        <Typography variant='heavy' sx={{ fontSize: '14px', color: status.color }}>{status.label}</Typography>
                    </Stack>
                    <ArrowForwardIosRounded sx={{ color: theme.palette.hold.main, fontSize: '14px' }} />
                </Stack>

                <Divider />

                <Stack spacing={0.5}>
                    <Stack spacing={0.25} sx={{ opacity: originalItemOpacity }}>
                        <Typography variant='normal' noWrap sx={{ fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.name}
                        </Typography>
                        {item.vendor?.name && (
                            <Typography variant='normal' sx={{ fontSize: '12px', color: theme.palette.subtext.main }}>
                                {item.vendor.name}
                            </Typography>
                        )}
                    </Stack>
                    <SwapVertRounded sx={{ alignSelf: 'center', color: theme.palette.hold.main, fontSize: '16px' }} />
                    {proposedItem.name && (
                        <Stack spacing={0.25} sx={{ opacity: proposedItemOpacity }}>
                            <Typography variant='normal' noWrap sx={{ color: theme.palette.blue.main, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {proposedItem.name}
                            </Typography>
                            {proposedItem.vendor?.name && (
                                <Typography variant='normal' sx={{ fontSize: '12px', color: theme.palette.subtext.main }}>
                                    {proposedItem.vendor.name}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Stack>

                <Divider />

                <Stack spacing={0.5}>
                    {replacement.replacement_value > 0 && (
                        <Stack direction='row' alignItems='center' spacing={0.75}>
                            <TrendingDownRounded sx={{ color: theme.palette.green.taskText, fontSize: '16px' }} />
                            <Typography variant='normal' sx={{ flex: 1, fontSize: '12px', color: theme.palette.subtext.main }}>Projected max savings /yr</Typography>
                            <Typography variant='heavy' sx={{ fontSize: '13px', color: theme.palette.green.taskText, whiteSpace: 'nowrap' }}>{formatCurrency(replacement.replacement_value, 0)}</Typography>
                        </Stack>
                    )}
                    {replacement.case_item_spend_current_item > 0 && (
                        <Stack direction='row' alignItems='center' spacing={0.75}>
                            <TrendingDownRounded sx={{ color: theme.palette.green.taskText, fontSize: '16px' }} />
                            <Typography variant='normal' sx={{ flex: 1, fontSize: '12px', color: theme.palette.subtext.main }}>Estimated off-contract spend reduction /yr</Typography>
                            <Typography variant='heavy' sx={{ fontSize: '13px', color: theme.palette.green.taskText, whiteSpace: 'nowrap' }}>{formatCurrency(replacement.case_item_spend_current_item, 0)}</Typography>
                        </Stack>
                    )}
                </Stack>

                {isPartiallyAccepted && (
                    <Chip
                        label={`${acceptedCount} of ${totalCount} accepted`}
                        size='small'
                        sx={{ alignSelf: 'flex-start', height: 20, backgroundColor: theme.palette.background.darkYellow, color: theme.palette.yellow.inProgress, fontSize: '11px' }}
                    />
                )}
            </Stack>

            <Divider />
            
            <Button
                variant={isOpen ? 'blueContained' : 'outlined'}
                startIcon={isOpen ? <SwapHorizRounded /> : <UndoRounded />}
                onClick={onClick}
                fullWidth
            >
                Review Replacement
            </Button>
        </Stack>
    );
}

export default ItemReplacementEntry;